/**
 * Ключ идемпотентности мутации кабинета.
 *
 * ЗАЧЕМ. Каждая мутация кабинета требует заголовка Idempotency-Key (8–255 символов,
 * apps/api/src/modules/booking/request-context.ts): без него сервер отвечает 400
 * с type .../idempotency-key-required. Пространство ключей — staff:<userId> плюс область
 * маршрута, и повтор с тем же телом возвращает ТОТ ЖЕ ответ, а не второй объект.
 *
 * ПОЧЕМУ КЛЮЧ ЖИВЁТ У ПОПЫТКИ, А НЕ У НАЖАТИЯ. Ключ обязан пережить повтор запроса
 * после обрыва: администратор нажал «Сохранить», ответ не доехал, кабинет повторяет —
 * и должен получить тот же результат, а не второго клиента с тем же телефоном. Поэтому
 * ключ создаётся ОДИН раз на попытку действия и передаётся во все повторы этой попытки,
 * а не генерируется внутри apiRequest на каждый вызов fetch.
 *
 * Отсюда же правило вызывающего кода: новый ключ — это новое НАМЕРЕНИЕ человека
 * («сохранить ещё раз, уже другое»), а не новая сетевая попытка того же намерения.
 */

/**
 * crypto.randomUUID есть и в браузере, и в jsdom, но только на защищённом источнике.
 * Запасной путь нужен не ради красоты: кабинет открывают по http на стенде разработки,
 * и падение на генерации ключа выключило бы там все мутации разом.
 */
function randomPart(): string {
  const crypto = globalThis.crypto as Crypto | undefined
  if (crypto !== undefined && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID()
  }
  if (crypto !== undefined && typeof crypto.getRandomValues === "function") {
    const bytes = crypto.getRandomValues(new Uint8Array(16))
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("")
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`
}

/**
 * Новый ключ попытки. Область в начале — не украшение: она видна в журнале сервера
 * и в поддержке отвечает на вопрос «что именно человек повторял», без заглядывания в тело.
 */
export function newIdempotencyKey(scope: string): string {
  return `${scope}-${randomPart()}`
}
